import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  Select,
  DatePicker,
  Alert,
  Tooltip
} from 'antd';
import { SearchOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { notificationTaskService } from '../../services/notificationTaskService';

const { Option } = Select;

const TaskExecutionLog: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [searchForm] = Form.useForm();
  const [searchParams, setSearchParams] = useState<{
    taskName?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  const loadLogs = async (page = 1, pageSize = 10, params = searchParams) => {
    try {
      setLoading(true);
      const response = await notificationTaskService.getAllLogs({
        page,
        pageSize,
        ...params
      });
      let list: any[] = [];
      let total = 0;
      
      if (response?.data?.list) {
        list = response.data.list;
        total = response.data.total || 0;
      } else if (response?.list) {
        list = response.list;
        total = response.total || 0;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
        total = list.length;
      }
      
      setLogs(list);
      setPagination({ page, pageSize, total });
    } catch (error) {
      console.error('加载执行日志失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const columns = [
    { 
      title: '任务名称', 
      dataIndex: 'taskName', 
      key: 'taskName', 
      width: 150,
      render: (name: string) => name || '-'
    },
    { 
      title: '接口地址', 
      dataIndex: 'apiUrl', 
      key: 'apiUrl',
      width: 200,
      ellipsis: true,
      render: (url: string, record: any) => (
        <Tooltip title={url || record.notificationContent}>
          {url || record.notificationContent || '-'}
        </Tooltip>
      )
    },
    { 
      title: '请求方式', 
      dataIndex: 'httpMethod', 
      key: 'httpMethod',
      width: 90,
      render: (method: string) => {
        if (!method) return '-';
        const methodMap: Record<string, string> = {
          'GET': 'green',
          'POST': 'blue',
          'PUT': 'orange',
          'DELETE': 'red'
        };
        return <Tag color={methodMap[method] || 'default'}>{method}</Tag>;
      }
    },
    { 
      title: '执行状态', 
      dataIndex: 'status', 
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
          'pending': { label: '待执行', color: 'default', icon: <SyncOutlined spin /> },
          'running': { label: '执行中', color: 'processing', icon: <SyncOutlined spin /> },
          'sent': { label: '成功', color: 'success', icon: <CheckCircleOutlined /> },
          'success': { label: '成功', color: 'success', icon: <CheckCircleOutlined /> },
          'failed': { label: '失败', color: 'error', icon: <CloseCircleOutlined /> },
          'read': { label: '已读', color: 'blue', icon: <CheckCircleOutlined /> }
        };
        const config = statusMap[status] || { label: status || '-', color: 'default', icon: null };
        return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>;
      }
    },
    { 
      title: '响应结果', 
      dataIndex: 'responseData', 
      key: 'responseData',
      ellipsis: true,
      render: (data: string, record: any) => {
        const content = data || record.errorMessage || '-';
        return (
          <Tooltip title={content}>
            <span style={{ color: record.status === 'failed' ? '#ff4d4f' : 'inherit' }}>
              {content.length > 50 ? content.substring(0, 50) + '...' : content}
            </span>
          </Tooltip>
        );
      }
    },
    { 
      title: '执行耗时', 
      dataIndex: 'duration', 
      key: 'duration',
      width: 100,
      render: (duration: number) => duration ? `${duration}ms` : '-'
    },
    { 
      title: '执行时间', 
      dataIndex: 'sentAt', 
      key: 'sentAt',
      width: 160,
      render: (time: string, record: any) => time || record.createdAt || '-'
    }
  ];

  const handleSearch = (values: any) => {
    const params = {
      taskName: values.taskName,
      status: values.status,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    setSearchParams(params);
    loadLogs(1, pagination.pageSize, params);
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    loadLogs(1, pagination.pageSize, {});
  };

  return (
    <Card title={<><FileTextOutlined /> 执行日志</>}>
      <Alert
        title="执行日志"
        description="查看定时任务的执行历史记录，包括执行状态、响应结果、执行耗时等信息。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form
        form={searchForm}
        layout="inline"
        style={{ marginBottom: 16 }}
        onFinish={handleSearch}
      >
        <Form.Item name="taskName">
          <Input placeholder="任务名称" allowClear style={{ width: 150 }} />
        </Form.Item>
        <Form.Item name="status">
          <Select placeholder="执行状态" allowClear style={{ width: 120 }}>
            <Option value="pending">待执行</Option>
            <Option value="running">执行中</Option>
            <Option value="success">成功</Option>
            <Option value="sent">已完成</Option>
            <Option value="failed">失败</Option>
          </Select>
        </Form.Item>
        <Form.Item name="dateRange">
          <DatePicker.RangePicker placeholder={['开始日期', '结束日期']} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>
      <Table
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => loadLogs(page, pageSize, searchParams)
        }}
        columns={columns}
        scroll={{ x: 1000 }}
      />
    </Card>
  );
};

export default TaskExecutionLog;
